import {
  env,
  SamModel,
  AutoProcessor,
  RawImage,
  Tensor,
  PreTrainedModel,
  Processor,
} from "@huggingface/transformers";

export type SamPoint = { x: number; y: number; label: 0 | 1 };

interface SamState {
  model: PreTrainedModel | null;
  processor: (Processor & { post_process_masks: (...args: any[]) => any }) | null;
  imageEmbeddings: Tensor | null;
  imagePositionalEmbeddings: Tensor | null;
  originalSizes: [number, number][] | null;
  reshapedInputSizes: [number, number][] | null;
  originalImageSize: { width: number; height: number } | null;
}

const state: SamState = {
  model: null,
  processor: null,
  imageEmbeddings: null,
  imagePositionalEmbeddings: null,
  originalSizes: null,
  reshapedInputSizes: null,
  originalImageSize: null,
};

let initPromise: Promise<void> | null = null;

export async function initSam(): Promise<void> {
  if (state.model && state.processor) return;
  if (initPromise) return initPromise;

  initPromise = (async () => {
    try {
      env.allowLocalModels = false;

      state.model = await SamModel.from_pretrained("Xenova/sam-vit-base");
      state.processor = await AutoProcessor.from_pretrained(
        "Xenova/sam-vit-base"
      ) as SamState["processor"];
    } catch (error) {
      state.model = null;
      state.processor = null;
      throw new Error(
        error instanceof Error
          ? error.message
          : "Failed to initialize SAM model"
      );
    } finally {
      initPromise = null;
    }
  })();

  return initPromise;
}

export async function encodeSamImage(imageFile: File): Promise<void> {
  if (!state.model || !state.processor) {
    throw new Error("SAM not initialized. Call initSam() first.");
  }

  const objectUrl = URL.createObjectURL(imageFile);
  try {
    const rawImage = await RawImage.fromURL(objectUrl);

    const processed = await state.processor(rawImage);
    const { pixel_values, original_sizes, reshaped_input_sizes } = processed;

    const embeddings = await (state.model as any).get_image_embeddings({
      pixel_values,
    });

    state.imageEmbeddings = embeddings.image_embeddings;
    state.imagePositionalEmbeddings = embeddings.image_positional_embeddings;
    state.originalSizes = original_sizes;
    state.reshapedInputSizes = reshaped_input_sizes;
    state.originalImageSize = { width: rawImage.width, height: rawImage.height };
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

export async function decodeSamMask(
  points: SamPoint[]
): Promise<{ mask: ImageData; score: number }> {
  if (
    !state.model ||
    !state.processor ||
    !state.imageEmbeddings ||
    !state.imagePositionalEmbeddings ||
    !state.originalSizes ||
    !state.reshapedInputSizes ||
    !state.originalImageSize
  ) {
    throw new Error(
      "No image encoded. Call encodeSamImage() first."
    );
  }

  const numPoints = points.length;

  // Build input_points tensor: [1, 1, N, 2]
  const pointCoords = new Float32Array(numPoints * 2);
  for (let i = 0; i < numPoints; i++) {
    pointCoords[i * 2] = points[i].x;
    pointCoords[i * 2 + 1] = points[i].y;
  }
  const input_points = new Tensor("float32", pointCoords, [1, 1, numPoints, 2]);

  // Build input_labels tensor: [1, 1, N]
  const labelValues = new BigInt64Array(numPoints);
  for (let i = 0; i < numPoints; i++) {
    labelValues[i] = BigInt(points[i].label);
  }
  const input_labels = new Tensor("int64", labelValues, [1, 1, numPoints]);

  const outputs = await (state.model as any)({
    image_embeddings: state.imageEmbeddings,
    image_positional_embeddings: state.imagePositionalEmbeddings,
    input_points,
    input_labels,
  });

  const { pred_masks, iou_scores } = outputs;

  // iou_scores shape: [1, 1, 3] - pick best mask
  const scores = iou_scores.data as Float32Array;
  let bestIdx = 0;
  let bestScore = scores[0];
  for (let i = 1; i < scores.length; i++) {
    if (scores[i] > bestScore) {
      bestScore = scores[i];
      bestIdx = i;
    }
  }

  // Post-process masks to original image size
  const upscaledMasks = await state.processor.post_process_masks(
    pred_masks,
    state.originalSizes,
    state.reshapedInputSizes,
    { mask_threshold: 0.0, binarize: false }
  );

  // upscaledMasks[0] shape: [1, 3, H, W] where H,W = original size
  const maskTensor = upscaledMasks[0];
  const { width, height } = state.originalImageSize;
  const maskData = maskTensor.data;

  // Extract the best mask (index bestIdx out of 3 channels)
  const channelSize = width * height;
  const channelOffset = bestIdx * channelSize;

  const imageData = new ImageData(width, height);
  const pixels = imageData.data;

  for (let i = 0; i < channelSize; i++) {
    const maskValue = maskData[channelOffset + i];
    if (maskValue > 0) {
      pixels[i * 4] = 255;     // R
      pixels[i * 4 + 1] = 255; // G
      pixels[i * 4 + 2] = 255; // B
      pixels[i * 4 + 3] = 128; // A - semi-transparent
    }
    // else stays 0,0,0,0 (transparent)
  }

  return { mask: imageData, score: bestScore };
}

export async function applySamMask(
  imageFile: File,
  maskData: ImageData
): Promise<File> {
  const objectUrl = URL.createObjectURL(imageFile);
  try {
    const img = await createImageBitmap(await fetch(objectUrl).then((r) => r.blob()));

    const canvas = document.createElement("canvas");
    canvas.width = img.width;
    canvas.height = img.height;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Could not get 2d context");

    ctx.drawImage(img, 0, 0);

    const pixelData = ctx.getImageData(0, 0, img.width, img.height);
    const pixels = pixelData.data;
    const mask = maskData.data;

    for (let i = 0; i < pixels.length / 4; i++) {
      // If mask pixel alpha is 0, make original pixel transparent
      if (mask[i * 4 + 3] === 0) {
        pixels[i * 4 + 3] = 0;
      }
    }

    ctx.putImageData(pixelData, 0, 0);

    const blob = await new Promise<Blob>((resolve, reject) =>
      canvas.toBlob(
        (blob) =>
          blob ? resolve(blob) : reject(new Error("Failed to create blob")),
        "image/png"
      )
    );

    const [fileName] = imageFile.name.split(".");
    return new File([blob], `${fileName}-sam-segmented.png`, {
      type: "image/png",
    });
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

export function resetSamState(): void {
  state.imageEmbeddings = null;
  state.imagePositionalEmbeddings = null;
  state.originalSizes = null;
  state.reshapedInputSizes = null;
  state.originalImageSize = null;
}

export function isSamInitialized(): boolean {
  return state.model !== null && state.processor !== null;
}
